import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { AlertCircle, LogIn, Play, KeyRound, Shield, User as UserIcon } from 'lucide-react';
import { Button, Card, Input } from '../components/ui';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';

// Imperative toast — bypasses React render tree entirely so nothing in the
// component hierarchy (CSS overrides, conditional renders, motion animations)
// can prevent the error from being shown to the user.
function showErrorToast(message: string) {
    // Guaranteed-visible fallback: native browser alert.
    // Some host environments (iframe, strict CSP, browser extensions) can hide
    // injected DOM elements, so we always fall back to a native alert which
    // browsers render in their own chrome layer that no page CSS can hide.
    try {
        const existing = document.getElementById('pp-login-error-toast');
        if (existing) existing.remove();

        const toast = document.createElement('div');
        toast.id = 'pp-login-error-toast';
        toast.setAttribute('role', 'alert');
        toast.setAttribute('aria-live', 'assertive');
        toast.style.cssText = [
            'position:fixed',
            'top:20px',
            'left:50%',
            'transform:translateX(-50%)',
            'z-index:2147483647',
            'background:#dc2626',
            'color:#ffffff',
            'padding:14px 22px',
            'border-radius:12px',
            'box-shadow:0 12px 32px rgba(0,0,0,0.28)',
            'font:600 14px/1.4 system-ui,-apple-system,Segoe UI,Roboto,sans-serif',
            'max-width:90vw',
            'display:flex',
            'align-items:center',
            'gap:12px',
            'pointer-events:auto',
        ].join(';');

        const icon = document.createElement('span');
        icon.textContent = '⚠';
        icon.style.cssText = 'font-size:18px;line-height:1';

        const text = document.createElement('span');
        text.textContent = message;

        const close = document.createElement('button');
        close.textContent = '×';
        close.setAttribute('aria-label', 'Dismiss');
        close.style.cssText = [
            'margin-left:8px',
            'background:rgba(255,255,255,0.22)',
            'color:#fff',
            'border:none',
            'border-radius:6px',
            'padding:2px 10px',
            'cursor:pointer',
            'font:700 16px/1 system-ui,sans-serif',
        ].join(';');
        close.onclick = () => toast.remove();

        toast.appendChild(icon);
        toast.appendChild(text);
        toast.appendChild(close);
        document.body.appendChild(toast);

        setTimeout(() => {
            if (toast.parentNode) toast.remove();
        }, 8000);
    } catch (err) {
        console.error('[Login] toast injection failed:', err);
        // Fallback to native alert if DOM injection fails for any reason
        try { window.alert(message); } catch { /* ignore */ }
    }
}

export default function Login() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { setToken, isAuthenticated, user } = useAuth();
    const { settings } = useSettings();

    const [error, setError] = useState('');
    const [demoMode, setDemoMode] = useState(false);
    const [username, setUsername] = useState(() => localStorage.getItem('login_remembered_username') || '');
    const [password, setPassword] = useState('');
    const [pwLoading, setPwLoading] = useState(false);
    const [rememberMe, setRememberMe] = useState(() => !!localStorage.getItem('login_remembered_username'));

    // Set page title for accessibility
    useEffect(() => {
        const previousTitle = document.title;
        document.title = `Staff Login | ${settings?.township_name || 'Municipality 311'}`;
        return () => {
            document.title = previousTitle;
        };
    }, [settings?.township_name]);

    // Check demo mode on mount
    useEffect(() => {
        const checkAuth = async () => {
            try {
                const demoRes = await fetch('/api/demo/info');
                if (demoRes.ok) {
                    const demoData = await demoRes.json();
                    setDemoMode(demoData.demo_mode === true);
                }
            } catch (err) {
                console.error('Failed to check demo status:', err);
            }
        };
        checkAuth();
    }, []);

    // Handle callback with token or error from Auth0
    useEffect(() => {
        const token = searchParams.get('token');
        const urlError = searchParams.get('error');
        
        if (token) {
            setToken(token);
            // Remove token from URL
            navigate('/login', { replace: true });
        } else if (urlError) {
            // Keep the error in the URL so it's resilient to refreshes, 
            // and simply display it in the UI.
            setError(decodeURIComponent(urlError.replace(/\+/g, ' ')));
        }
    }, [searchParams, setToken, navigate]);

    // Redirect if already logged in
    useEffect(() => {
        if (isAuthenticated && user) {
            navigate(user.role === 'admin' ? '/admin' : '/staff');
        }
    }, [isAuthenticated, user, navigate]);

    const handlePasswordLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setPwLoading(true);
        try {
            const res = await fetch('/api/auth/password-login', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ username: username.trim(), password, remember_me: rememberMe }),
            });
            let data: any = null;
            try {
                data = await res.json();
            } catch {
                data = null;
            }
            if (!res.ok) {
                let msg = 'Invalid username or password';
                if (data && typeof data.detail === 'string') {
                    msg = data.detail;
                } else if (res.status === 401) {
                    msg = 'Invalid username or password';
                } else if (res.status === 403) {
                    msg = 'Account is disabled';
                } else if (res.status >= 500) {
                    msg = 'Server error. Please try again later.';
                }
                console.warn('[Login] failed:', res.status, msg);
                setError(msg);
                showErrorToast(msg);
                return;
            }
            // Persist or clear remembered username based on checkbox
            if (rememberMe) {
                localStorage.setItem('login_remembered_username', username.trim());
            } else {
                localStorage.removeItem('login_remembered_username');
            }
            await setToken(data.access_token);
        } catch (err) {
            console.error('[Login] error:', err);
            const msg = (err as Error)?.message || 'Login failed. Please check your connection and try again.';
            setError(msg);
            showErrorToast(msg);
        } finally {
            setPwLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center p-4">
            {/* Top-of-page error toast — guaranteed visible */}
            {error && (
                <div
                    role="alert"
                    aria-live="assertive"
                    style={{
                        position: 'fixed',
                        top: 16,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        zIndex: 9999,
                        background: '#dc2626',
                        color: '#ffffff',
                        padding: '12px 20px',
                        borderRadius: 12,
                        boxShadow: '0 10px 30px rgba(0,0,0,0.25)',
                        fontWeight: 600,
                        fontSize: 14,
                        maxWidth: '90vw',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                    }}
                >
                    <AlertCircle className="w-5 h-5" />
                    <span>{error}</span>
                    <button
                        onClick={() => setError('')}
                        style={{
                            marginLeft: 8,
                            background: 'rgba(255,255,255,0.2)',
                            color: '#fff',
                            border: 'none',
                            borderRadius: 6,
                            padding: '2px 8px',
                            cursor: 'pointer',
                            fontWeight: 700,
                        }}
                        aria-label="Dismiss"
                    >
                        ×
                    </button>
                </div>
            )}
            {/* Main content landmark */}
            <main id="main-content" className="w-full max-w-md">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                >
                    <Card className="p-8">
                        {/* Logo */}
                        <div className="text-center mb-8">
                            {settings?.logo_url ? (
                                <img
                                    src={settings.logo_url}
                                    alt={`${settings?.township_name || 'Municipality'} logo`}
                                    className="h-16 mx-auto mb-4"
                                />
                            ) : (
                                <div className="h-16 w-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-indigo-400 to-indigo-600 flex items-center justify-center shadow-lg shadow-indigo-500/30">
                                    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" fill="white" opacity="0.95"/>
                                        <circle cx="12" cy="9" r="3" fill="url(#pinGrad)"/>
                                        <defs>
                                            <linearGradient id="pinGrad" x1="9" y1="6" x2="15" y2="12" gradientUnits="userSpaceOnUse">
                                                <stop stopColor="#818cf8"/>
                                                <stop offset="1" stopColor="#4f46e5"/>
                                            </linearGradient>
                                        </defs>
                                    </svg>
                                </div>
                            )}
                            <h1 className="text-2xl font-bold text-white" data-no-translate>
                                {settings?.township_name || 'Municipality 311'}
                            </h1>
                            <p className="text-white/50 mt-2">Staff Access Portal</p>
                        </div>

                        {/* Global Error Message */}
                        {error && (
                            <div
                                className="flex items-center gap-3 p-4 rounded-xl mb-6"
                                style={{ background: '#fef2f2', border: '1px solid #f87171', color: '#b91c1c' }}
                                role="alert"
                                aria-live="assertive"
                            >
                                <AlertCircle className="w-5 h-5 flex-shrink-0" style={{ color: '#ef4444' }} aria-hidden="true" />
                                <span className="text-sm font-medium">{error}</span>
                            </div>
                        )}

                        {/* Demo Mode Login */}
                        {demoMode ? (
                            <div className="space-y-4">
                                <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4 mb-2">
                                    <p className="text-emerald-300 font-medium text-center">🎯 Live Demo Environment</p>
                                    <p className="text-emerald-200/70 text-sm mt-1 text-center">
                                        Explore the full staff dashboard and admin console.
                                    </p>
                                </div>
                                <a
                                    href="/api/auth/demo-login"
                                    className="flex items-center justify-center gap-2 w-full px-6 py-3 rounded-xl font-medium text-white bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 transition-all shadow-lg shadow-emerald-500/20"
                                >
                                    <Play className="w-5 h-5" />
                                    Enter Demo
                                </a>
                            </div>
                        ) : (
                            <>
                                {/* Username & Password Login Form */}
                                <form onSubmit={handlePasswordLogin} className="space-y-4" autoComplete="on">
                                    <div>
                                        <label htmlFor="login-username" className="block text-sm font-medium text-white/70 mb-1.5">Username or email</label>
                                        <div className="relative">
                                            <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40 pointer-events-none" />
                                            <Input
                                                id="login-username"
                                                type="text"
                                                autoComplete="username"
                                                value={username}
                                                onChange={(e) => setUsername(e.target.value)}
                                                placeholder="yourname"
                                                className="pl-9"
                                                required
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <label htmlFor="login-password" className="block text-sm font-medium text-white/70 mb-1.5">Password</label>
                                        <div className="relative">
                                            <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40 pointer-events-none" />
                                            <Input
                                                id="login-password"
                                                type="password"
                                                autoComplete="current-password"
                                                value={password}
                                                onChange={(e) => setPassword(e.target.value)}
                                                placeholder="••••••••"
                                                className="pl-9"
                                                required
                                            />
                                        </div>
                                    </div>
                                    <label className="flex items-center gap-2 cursor-pointer select-none text-sm" style={{ color: '#5b6a85' }}>
                                        <input
                                            type="checkbox"
                                            checked={rememberMe}
                                            onChange={(e) => setRememberMe(e.target.checked)}
                                            className="w-4 h-4 rounded cursor-pointer"
                                            style={{ accentColor: '#0fb37a' }}
                                        />
                                        <span>Remember me on this device (30 days)</span>
                                    </label>
                                    <Button
                                        type="submit"
                                        size="lg"
                                        className="w-full"
                                        disabled={pwLoading || !username || !password}
                                        isLoading={pwLoading}
                                        leftIcon={<LogIn className="w-5 h-5" />}
                                    >
                                        Sign In
                                    </Button>
                                </form>

                                <div className="mt-4 flex items-center justify-center gap-2 text-white/40 text-xs">
                                    <Shield className="w-3.5 h-3.5" />
                                    <span>Secure password authentication</span>
                                </div>
                            </>
                        )}

                        {/* Footer */}
                        <div className="mt-8 pt-6 border-t border-white/10 text-center">
                            <p className="text-sm text-white/40">
                                Authorized users only. Contact an administrator to request access.
                            </p>
                            <Link
                                to="/"
                                className="inline-block mt-4 text-sm text-primary-400 hover:text-primary-300 transition-colors"
                            >
                                <span aria-hidden="true">←</span> Back to public portal
                            </Link>
                            <p className="text-white/20 text-xs mt-4">
                                Powered by{' '}
                                <a
                                    href="https://pinpoint311.org"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-primary-400/50 hover:text-primary-300 transition-colors inline-flex items-center gap-1"
                                    data-no-translate
                                >
                                    Pinpoint 311
                                </a>
                                {' '}— Free &amp; Open Source
                            </p>
                        </div>
                    </Card>
                </motion.div>
            </main>
        </div>
    );
}

